import { FC, ReactNode, useEffect, useState, useCallback } from 'react';
import { Box, BoxProps, Flex, Spinner, Text } from '@chakra-ui/react';
import { Table, Thead, Tbody, Th, Tr, Td } from '@chakra-ui/react';
import { CbTableHeaderText } from '../CbTable/CbTableHeaderText';
import { CbTablePagination } from '../CbTable/CbTablePagination';
import { TableColumn } from '../../types/TableColumn';
import { CbGenericTableRow } from './CbGenericTableRow';
export interface CbGenericTableProps extends BoxProps {
  currentPage?: number;
  columns: TableColumn[];
  disableFooter?: boolean;
  initialActiveKey?: string;
  isDarkMode?: boolean;
  isSortable?: boolean;
  label?: string;
  rowData: any;
  tableHeight?: string;
  subText?: string;
  paddingX?: string;
  isPaginated?: boolean;
  showTotalEntries?: boolean;
  customFooter?: ReactNode;
  onSortChange?: (key: string, isAscending: boolean) => void;
  defaultSortDirections?: {
    [key: string]: 'ASC' | 'DESC';
  };
  isLoading: boolean;
  trailingRowElement?: () => ReactNode;
  /* These three params are required if you want pagination */
  onPageChange?: (page: string | number) => void;
  numberOfRows?: number;
  totalEntries?: number;
}
export const CbGenericTable: FC<CbGenericTableProps> = ({
  currentPage,
  columns,
  disableFooter = false,
  initialActiveKey = '',
  isDarkMode,
  isSortable,
  label,
  numberOfRows,
  rowData,
  tableHeight,
  totalEntries,
  onPageChange,
  onSortChange,
  subText,
  paddingX,
  isPaginated,
  showTotalEntries,
  customFooter = <></>,
  defaultSortDirections = {},
  isLoading,
  trailingRowElement,
  ...props
}) => {
  const [activeKey, setActiveKey] = useState(initialActiveKey);
  const [sortDirections, setSortDirections] = useState(defaultSortDirections);
  const alternateRowColor = isDarkMode ? 'darkMode.700' : 'cloud.200';
  const backgroundColor = isDarkMode ? 'darkMode.500' : 'white';
  const borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'cloud.400';
  const textColor = isDarkMode ? 'text.primaryWhite' : 'text.primary';
  const isColumnAscending = useCallback((columnKey: string) => {
    if (!sortDirections[columnKey]) {
      setSortDirections({
        ...sortDirections,
        [columnKey]: 'ASC'
      });
      return true;
    }
    return sortDirections[columnKey] === 'ASC';
  }, [sortDirections, setSortDirections]);
  const setColumnAscending = useCallback((columnKey: string, isAscending: boolean) => {
    setSortDirections({
      ...sortDirections,
      [columnKey]: isAscending ? 'ASC' : 'DESC'
    });
    return isAscending;
  }, [setSortDirections]);
  const handleHeaderClick = useCallback((key: string) => {
    let ascending = isColumnAscending(key);
    ascending = setColumnAscending(key, !ascending);
    setActiveKey(key);
    onSortChange?.(key, ascending);
  }, [setActiveKey, isColumnAscending, setColumnAscending]);
  useEffect(() => {
    if (initialActiveKey && activeKey) {
      onSortChange?.(activeKey, isColumnAscending(activeKey));
    }
  }, [initialActiveKey]);
  const isAscending = isColumnAscending(activeKey);
  const visibleColumns = columns.filter(col => !col.isHidden);
  return <>
      <Box backgroundColor={backgroundColor} borderRadius={'3xl'} boxShadow="standardCard" border="1px solid" borderColor={borderColor} overflowX="hidden" minHeight={tableHeight} {...props} sx={{
      '::-webkit-scrollbar': {
        display: 'none'
      }
    }} data-sentry-element="Box" data-sentry-source-file="CbGenericTable.tsx">
        {isLoading ? <Box height={tableHeight}>{renderLoadingState(backgroundColor)}</Box> : <Box overflow="auto" width="100%">
            <Table width="100%">
              <Thead position="sticky" top={0} backgroundColor={backgroundColor} boxShadow="standardCard">
                <Tr borderBottom="1px solid" borderColor={borderColor}>
                  {!!rowData && visibleColumns && visibleColumns.map((header, index) => header.hasCustomTitleElement ? <Th borderBottom="none" key={header?.title || index}>
                          {header?.titleElement}
                        </Th> : <CbTableHeaderText key={header?.title} columnKey={header?.key || String(index)} fontFamily="nunitosans" fontSize="md" isActive={activeKey === header?.key} isAscending={isAscending} isDarkMode={isDarkMode} isUppercase={false} isSortable={!!header?.isSortable} text={header?.title} textColor={textColor} paddingX={{
                base: 2,
                lg: paddingX || 4
              }} paddingTop={{
                base: 2,
                lg: 4
              }} paddingBottom={{
                base: 2,
                lg: 3
              }} onHeaderClick={handleHeaderClick} />)}
                  {!rowData && <CbTableHeaderText key={visibleColumns[0]?.title} columnKey={visibleColumns[0]?.key} fontFamily="nunitosans" fontSize="md" isActive={activeKey === visibleColumns[0]?.key} isAscending={isAscending} isDarkMode={isDarkMode} isUppercase={false} isSortable={isSortable} text={visibleColumns[0]?.title} textColor={textColor} paddingX={paddingX || '18px'} paddingTop={4} paddingBottom={3} onHeaderClick={handleHeaderClick} />}
                </Tr>
              </Thead>
              <Tbody>
                {rowData?.map((row: any, index: number) => {
              const isOddRow = (index + 1) % 2;
              if (!row) {
                return null;
              } else {
                return <CbGenericTableRow key={index} columns={visibleColumns} isOddRow={isOddRow} alternateRowColor={alternateRowColor} indexKey={index} borderColor={borderColor} row={row} paddingX={paddingX || '18px'} textColor={textColor} isHighlighted={row?.isHighlighted || false} isCustomRow={row.isCustomRow || false} customRowComponent={row?.customRowComponent} />;
              }
            })}
                {trailingRowElement ? <Tr>
                    <Td colSpan={visibleColumns.length} borderTop={'1px solid'} borderColor={borderColor}>
                      {trailingRowElement()}
                    </Td>
                  </Tr> : null}
                {!rowData && !isLoading && <Tr backgroundColor="gray.100" key="1" width="100%" height={tableHeight}>
                    <Td height={tableHeight} width="100%" textAlign="center" verticalAlign="center">
                      <Text fontFamily="codenext" color="cloud.500" fontSize="28px">
                        {'Nothing to show'}
                      </Text>

                      <Text color="cloud.800" fontSize="18px">
                        {subText}
                      </Text>
                    </Td>
                  </Tr>}
              </Tbody>
            </Table>
          </Box>}
        {!disableFooter && <Flex alignItems="center" justifyContent="space-between" borderTop="1px solid" borderColor={borderColor} backgroundColor={backgroundColor} borderBottomRadius={24} height="54px" paddingX={paddingX || '18px'} position="sticky" bottom={0}>
            {customFooter}
            {!!onPageChange && !!numberOfRows && !!totalEntries && <Box width="100%">
                <CbTablePagination buttonStyle="outline" buttonSize="34px" currentPage={currentPage} fontSize="sm" hasPagesPopover={false} isDarkMode={isDarkMode} label={label} numberOfRows={numberOfRows} totalEntries={totalEntries} handlePageChange={onPageChange} />
              </Box>}
          </Flex>}
      </Box>
    </>;
};
function renderLoadingState(backgroundColor: string) {
  return <Flex width="100%" height="100%" justifyContent="center" alignItems="center" backgroundColor={backgroundColor} data-sentry-element="Flex" data-sentry-component="renderLoadingState" data-sentry-source-file="CbGenericTable.tsx">
      <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="chonky.500" size="lg" data-sentry-element="Spinner" data-sentry-source-file="CbGenericTable.tsx" />
    </Flex>;
}